import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import Badge from '../components/ui/Badge';
import supabase from '../supabase/supabase';

const Home = () => {
  const [news, setNews] = useState([]);
  const [tools, setTools] = useState([]);
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHomeData = async () => {
      try {
        const { data: newsData } = await supabase
          .from('ai_news')
          .select('*')
          .eq('status', 'published')
          .eq('is_featured', true)
          .order('created_at', { ascending: false })
          .limit(4);

        if (newsData) setNews(newsData);

        const { data: toolsData } = await supabase
          .from('ai_tools')
          .select('*')
          .eq('status', 'published')
          .eq('is_featured', true)
          .order('created_at', { ascending: false })
          .limit(3);

        if (toolsData) setTools(toolsData);

        const { data: threadsData } = await supabase
          .from('viral_threads')
          .select('*')
          .eq('status', 'published')
          .eq('is_featured', true)
          .order('created_at', { ascending: false })
          .limit(2);

        if (threadsData) setThreads(threadsData);

      } catch (error) {
        console.error("Error fetching home data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchHomeData();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-32">
        <SafeIcon icon="Loader" className="w-8 h-8 text-brand-text animate-spin" />
      </div>
    );
  }

  return (
    <div className="w-full max-w-[800px] mx-auto px-4 sm:px-6 py-8 space-y-10">
      
      {/* Intro Box - Newsletter Style */}
      <div className="bg-brand-surface border-2 border-brand-border p-6 md:p-8">
        <h1 className="text-[26px] md:text-[32px] font-bold text-brand-text mb-4 leading-tight">
          Good morning, AI enthusiasts.
        </h1>
        <p className="text-[18px] md:text-[20px] text-brand-secondary leading-[1.55] mb-6">
          Welcome to Pulse5. We strip away the noise so you can see the signal. Discover the biggest AI stories, the newest tools, and viral insights — completely fluff-free.
        </p>
        
        {news.length > 0 && (
          <>
            <div className="text-[18px] md:text-[20px] font-bold text-brand-text mb-3">
              Today's Pulse:
            </div>
            <ul className="space-y-3">
              {news.slice(0, 3).map((item) => (
                <li key={item.id} className="flex items-start gap-3 text-[18px] md:text-[20px] text-brand-secondary leading-[1.55]">
                  <span className="text-brand-text mt-2 w-2 h-2 bg-brand-text rounded-full flex-shrink-0"></span>
                  <span>{item.hook || item.title}</span>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      {/* LATEST AI NEWS SECTION */}
      {news.length > 0 && (
        <section className="space-y-0">
          <div className="bg-brand-text text-brand-bg font-bold py-3 px-4 text-center uppercase tracking-widest text-[13px] md:text-[14px]">
            Critical Intel
          </div>
          
          <div className="border-x-2 border-b-2 border-brand-border bg-brand-bg divide-y-2 divide-brand-border">
            {news.map(item => {
              const displayCategory = (item.category || 'AI News').split(',')[0].trim();
              
              return (
                <article key={item.id} className="p-6 md:p-8 bg-brand-surface">
                  <div className="flex items-center gap-2 text-[13px] md:text-[14px] font-mono text-brand-secondary uppercase tracking-widest mb-3">
                    <SafeIcon icon="Search" className="w-4 h-4" />
                    <span className="font-bold text-brand-text">{displayCategory}</span>
                  </div>
                  
                  <h2 className="text-[26px] md:text-[32px] lg:text-[36px] font-bold text-brand-blue leading-tight mb-3 underline decoration-brand-blue/30 hover:decoration-brand-blue underline-offset-4">
                    {item.hook || item.title}
                  </h2>
                  
                  <p className="text-[18px] md:text-[20px] text-brand-secondary leading-[1.55] mb-4">
                    {item.summary}
                  </p>

                  {item.points && item.points.length > 0 && item.points[0] !== "" && (
                    <ul className="space-y-2 mb-4">
                      {item.points.map((point, idx) => point ? (
                        <li key={idx} className="flex items-start gap-2.5 text-[18px] md:text-[20px] text-brand-secondary leading-[1.55]">
                          <span className="text-brand-text font-bold mt-0.5 md:mt-1">•</span>
                          <span>{point}</span>
                        </li>
                      ) : null)}
                    </ul>
                  )}

                  {item.why_it_matters && (
                    <div className="mt-4 pt-4 border-t border-brand-border/30">
                      <span className="font-bold text-brand-text text-[18px] md:text-[20px] mr-2">Why it matters:</span>
                      <span className="text-[18px] md:text-[20px] text-brand-secondary leading-[1.55]">{item.why_it_matters}</span>
                    </div>
                  )}
                  
                  {item.cta_text && item.cta_url && (
                    <a href={item.cta_url} target="_blank" rel="noopener noreferrer" className="mt-5 inline-block text-[16px] md:text-[18px] font-bold text-brand-blue underline hover:opacity-80 underline-offset-2">
                      {item.cta_text} →
                    </a>
                  )}
                </article>
              );
            })}
          </div>
          <div className="pt-4 text-center">
            <Link to="/news" className="text-[16px] md:text-[18px] font-bold text-brand-text hover:text-brand-blue transition-colors underline underline-offset-4">
              Read all news
            </Link>
          </div>
        </section>
      )}

      {/* LATEST AI TOOLS SECTION */}
      {tools.length > 0 && (
        <section className="space-y-0">
          <div className="bg-brand-text text-brand-bg font-bold py-3 px-4 text-center uppercase tracking-widest text-[13px] md:text-[14px]">
            Latest AI Tools
          </div>
          
          <div className="border-x-2 border-b-2 border-brand-border bg-brand-bg divide-y-2 divide-brand-border">
            {tools.map(tool => {
              const showHook = tool.hook && (!tool.name || tool.hook.trim().toLowerCase() !== tool.name.trim().toLowerCase());

              return (
                <article key={tool.id} className="p-6 md:p-8 bg-brand-surface flex flex-col md:flex-row gap-6 items-start">
                  {tool.logo && (
                    <div className="w-16 h-16 rounded-lg bg-brand-raised border border-brand-border flex items-center justify-center flex-shrink-0 overflow-hidden">
                      <img src={tool.logo} alt={tool.name} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-3 mb-2">
                      <h2 className="text-[26px] md:text-[32px] font-bold text-brand-text leading-tight">
                        {tool.name}
                      </h2>
                      {tool.badges && tool.badges.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-1">
                          {tool.badges.map(badge => (
                            <Badge key={badge} variant={badge === 'Trending' ? 'trending' : 'new'}>
                              {badge}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    {showHook && <p className="text-[20px] md:text-[24px] font-bold text-brand-secondary mb-3 leading-snug">{tool.hook}</p>}
                    <p className="text-[18px] md:text-[20px] text-brand-secondary leading-[1.55] mb-5 whitespace-pre-wrap">{tool.description}</p>
                    
                    {/* Deep Links Section */}
                    {tool.deep_links && tool.deep_links.length > 0 && (
                      <div className="mb-5 grid gap-2 pointer-events-auto">
                        {tool.deep_links.map((link, idx) => (
                          <a key={idx} href={link.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-3 bg-brand-bg border border-brand-border rounded-[8px] hover:border-brand-mint transition-colors group">
                            {link.thumbnail && (
                              <img src={link.thumbnail} alt={link.title} className="w-10 h-10 rounded-[6px] object-cover bg-brand-surface flex-shrink-0" />
                            )}
                            <div className="flex items-center gap-2">
                              {!link.thumbnail && <SafeIcon icon="Link" className="w-4 h-4 text-brand-muted group-hover:text-brand-mint transition-colors" />}
                              <span className="text-[16px] md:text-[18px] font-bold text-brand-text group-hover:text-brand-mint transition-colors line-clamp-2">
                                {link.title || 'Visit Link'}
                              </span>
                            </div>
                          </a>
                        ))}
                      </div>
                    )}

                    {/* Details (Best For / Features) */}
                    {(tool.best_for?.length > 0 || tool.features?.length > 0) && (
                      <div className="space-y-4 mb-6">
                        {tool.best_for?.length > 0 && (
                          <div>
                            <h4 className="text-[14px] md:text-[16px] font-mono font-bold text-brand-muted uppercase tracking-widest mb-2">Best For</h4>
                            <p className="text-[18px] md:text-[20px] text-brand-secondary">
                              {tool.best_for.join(' · ')}
                            </p>
                          </div>
                        )}
                        {tool.features?.length > 0 && (
                          <div>
                            <h4 className="text-[14px] md:text-[16px] font-mono font-bold text-brand-muted uppercase tracking-widest mb-2">Features</h4>
                            <ul className="space-y-2">
                              {tool.features.map((feature, idx) => (
                                <li key={idx} className="text-[18px] md:text-[20px] text-brand-secondary flex items-start gap-2.5">
                                  <SafeIcon icon="Check" className="w-4 h-4 text-brand-mint mt-1 flex-shrink-0" />
                                  <span className="leading-[1.55]">{feature}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Footer Actions */}
                    <div className="mt-4 pt-5 border-t border-brand-border flex items-center justify-between pointer-events-auto">
                      <div className="flex flex-col">
                        <span className="text-[16px] md:text-[18px] font-bold text-brand-text">{tool.pricing || 'Free / Paid'}</span>
                        {tool.is_affiliate && (
                          <span className="text-[12px] md:text-[14px] text-brand-muted uppercase tracking-wider">Affiliate link</span>
                        )}
                      </div>
                      
                      {tool.cta_text && tool.cta_url && (
                        <a href={tool.cta_url} target="_blank" rel="noopener noreferrer" className="px-5 py-2.5 rounded-md text-[16px] md:text-[18px] font-bold bg-brand-text text-brand-bg hover:bg-brand-secondary transition-colors flex items-center gap-2">
                          {tool.cta_text}
                          <SafeIcon icon="ExternalLink" className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
          <div className="pt-4 text-center">
            <Link to="/tools" className="text-[16px] md:text-[18px] font-bold text-brand-text hover:text-brand-blue transition-colors underline underline-offset-4">
              Explore all tools
            </Link>
          </div>
        </section>
      )}

      {/* EDITOR'S PICKS / THREADS SECTION */}
      {threads.length > 0 && (
        <section className="space-y-0">
          <div className="bg-brand-text text-brand-bg font-bold py-3 px-4 text-center uppercase tracking-widest text-[13px] md:text-[14px]">
            Editor's Picks
          </div>
          
          <div className="border-x-2 border-b-2 border-brand-border bg-brand-bg divide-y-2 divide-brand-border">
            {threads.map(thread => (
              <article key={thread.id} className="p-6 md:p-8 bg-brand-surface">
                <div className="flex items-center gap-2 text-[13px] md:text-[14px] font-mono text-brand-secondary uppercase tracking-widest mb-3">
                  <span className="font-bold text-brand-text">{thread.platform || 'Insight'}</span>
                  {thread.author && <span>• By {thread.author}</span>}
                </div>
                
                <h2 className="text-[26px] md:text-[32px] font-bold text-brand-text leading-tight mb-3">
                  {thread.title}
                </h2>
                
                <p className="text-[18px] md:text-[20px] text-brand-secondary leading-[1.55] whitespace-pre-wrap">
                  {thread.content}
                </p>

                {thread.cta_text && thread.cta_url && (
                  <a href={thread.cta_url} target="_blank" rel="noopener noreferrer" className="mt-5 inline-block text-[16px] md:text-[18px] font-bold text-brand-blue underline hover:opacity-80 underline-offset-2">
                    {thread.cta_text} →
                  </a>
                )}
              </article>
            ))}
          </div>
          <div className="pt-4 text-center">
            <Link to="/threads" className="text-[16px] md:text-[18px] font-bold text-brand-text hover:text-brand-blue transition-colors underline underline-offset-4">
              View all insights
            </Link>
          </div>
        </section>
      )}

    </div>
  );
};

export default Home;